import {
  UpsellFlow,
  useDeletePageMutation,
  useDeleteUpsellFlowMutation,
  useModifyPageTextsMutation,
  useModifyUpsellPageProductsMutation,
  useReorderUpsellFlowPagesMutation,
} from "../../../../store/api/upsellApi";
import { useSalesareaCtx } from "../../modernTS/salesarea/SalesareaContext";
import usePickTimeschedule from "../../modernTS/JUI/Timeschedules/usePickTimeschedule";
import { Typography } from "@mui/material";
import ModelInstance from "../../modernTS/JUI/ModelInstance";
import { UpsellFlowPages } from "./UpsellFlow";
import LoadingButton from "@mui/lab/LoadingButton";
import React from "react";
import { useHookForm } from "mui-react-hook-form-plus";
import { FormProvider, useFieldArray } from "react-hook-form";
import { toast } from "react-toastify";
import { usePinnableItems } from "../../modernTS/JUI/PinnableItems/usePinnableItems";
import Box from "@mui/material/Box";
import _ from "lodash";
import { TranslationsStorage } from "../../../../components/TranslatableEditor/TranslatableEditor.d";
import { TimescheduleLeaf } from "../../modernTS/JUI/Timeschedules/TimescheduleLeaf";

export function FlowEditPage({ flow }: { flow: UpsellFlow }) {
  const { id } = useSalesareaCtx();
  const openTimeschedulePicker = usePickTimeschedule(id, { allowAlwaysActive: true });
  const form = useHookForm({
    defaultValues: flow,
  });
  const isAlwaysActiveFlow = flow.time_schedule == null;
  const { unpin } = usePinnableItems();

  const pages = useFieldArray({
    control: form.control,
    name: "pages",
    keyName: "genId",
  });

  const [deleteFlow, { isLoading: flowDeletionIsLoading }] = useDeleteUpsellFlowMutation();
  const [reorderUpsellFlowPages] = useReorderUpsellFlowPagesMutation();
  const [modifyUpsellPageProducts] = useModifyUpsellPageProductsMutation();
  const [updateUpsellPageTexts] = useModifyPageTextsMutation();
  const [deletePage] = useDeletePageMutation();

  const onTextChange = _.debounce(
    (newText: TranslationsStorage, fieldName: "name" | "description", pageIndex: number) => {
      const page = form.getValues(`pages.${pageIndex}`);
      updateUpsellPageTexts({
        pageId: page!.id,
        texts: {
          [fieldName]: newText,
        },
      });
    },
    3300
  );

  /**
   * What would you want to be able to edit inside a flow?
   * - Delete the flow
   * - Change the timeschedule to a different one
   * - Modify its pages in any way
   *   - Add a page
   *   - Delete a page
   *   - Change page's name
   *   - Change page's description
   *   - Reorder pages
   *   - Change page's products or menus
   */
  return (
    <FormProvider {...form}>
      <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: 1, w: 1, gap: 4 }}>
        <Typography fontSize={"1.8em"} sx={{ gridColumn: "span 2" }} variant={"h4"}>
          <ModelInstance instance={flow} mark={"none"} modelName={"UpsellFlow"} showIdWhen={"always"} /> —{" "}
          <TimescheduleLeaf
            editable
            onClick={async () => {
              const newTimeschedule = await openTimeschedulePicker({
                disableItem: (timeschedule) => {
                  return false;
                },
              });
            }}
            timeschedule={flow.time_schedule}
          />
        </Typography>
        <UpsellFlowPages
          droppableId={`flow-pages-edit-${flow.id}`}
          hidden={false}
          onAppend={() =>
            pages.append({
              name: {},
              upsell_page_variant: "suggestion",
              products: [],
              description: {},
            })
          }
          onMove={async (from, to) => {
            pages.move(from, to);
            try {
              await reorderUpsellFlowPages({
                upsellFlowId: flow.id,
                order: form.getValues("pages").map((page) => page.id),
              }).unwrap();
              toast.success("Reordered pages!");
            } catch (e) {
              toast.error("Was unable to reorder pages :(");
            }
          }}
          onPageDeleted={async ({ pageIndex, id }) => {
            pages.remove(pageIndex);
            if (id) {
              try {
                await deletePage({
                  pageId: id,
                }).unwrap();
                toast.success(() => (
                  <Typography variant={"body2"}>Successfully removed page {pageIndex + 1}!</Typography>
                ));
              } catch (e) {
                toast.error(() => (
                  <Typography variant={"body2"}>Was unable to remove page {pageIndex + 1}!</Typography>
                ));
              }
            }
          }}
          onProductsAdded={async (newProducts, pageIndex) => {
            const key = `pages.${pageIndex}.products` as const;
            const products = [...form.getValues(key)].concat(newProducts);
            form.setValue(key, products);
            try {
              const page = form.getValues(`pages.${pageIndex}`);
              await modifyUpsellPageProducts({
                pageId: page.id,
                product_ids: products.map((p) => p.id),
              }).unwrap();
              toast.success(() => (
                <Typography variant={"body2"}>
                  Successfully added {newProducts.length} product(s) to{" "}
                  <ModelInstance
                    inheritColor
                    instance={flow}
                    mark={"none"}
                    modelName={"UpsellFlow"}
                    showIdWhen={"always"}
                  />
                  !
                </Typography>
              ));
            } catch (e) {
              toast.error(() => (
                <Typography variant={"body2"}>
                  Was unable to add products to
                  <ModelInstance
                    inheritColor
                    instance={flow}
                    mark={"none"}
                    modelName={"UpsellFlow"}
                    showIdWhen={"always"}
                  />
                </Typography>
              ));
            }
          }}
          onProductsRemoved={async (productIds, pageIndex) => {
            const key = `pages.${pageIndex}.products` as const;
            const products = [...form.getValues(key).filter((p) => !productIds.includes(p.id))];
            form.setValue(key, products);
            try {
              const page = form.getValues(`pages.${pageIndex}`);
              await modifyUpsellPageProducts({
                pageId: page.id,
                product_ids: products.map((p) => p.id),
              }).unwrap();
              toast.success(() => (
                <Typography variant={"body2"}>
                  Successfully removed {productIds.length} products from{" "}
                  <ModelInstance
                    inheritColor
                    instance={flow}
                    mark={"none"}
                    modelName={"UpsellFlow"}
                    showIdWhen={"always"}
                  />
                  !
                </Typography>
              ));
            } catch (e) {
              toast.error(() => (
                <Typography variant={"body2"}>
                  Was unable to remove {productIds.length} products from{" "}
                  <ModelInstance
                    inheritColor
                    instance={flow}
                    mark={"none"}
                    modelName={"UpsellFlow"}
                    showIdWhen={"always"}
                  />
                </Typography>
              ));
            }
          }}
          onTextChanged={onTextChange}
          pages={pages.fields ?? []}
        />
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 5fr", gap: 2 }}>
          {isAlwaysActiveFlow ? null : (
            <LoadingButton
              color={"error"}
              loading={flowDeletionIsLoading}
              onClick={async () => {
                try {
                  await deleteFlow(flow.id).unwrap();
                  unpin();
                  toast.success(() => {
                    return (
                      <Typography variant={"body2"}>
                        The configuration for pages shown at{" "}
                        <ModelInstance
                          inheritColor
                          instance={flow}
                          mark={"none"}
                          modelName={"UpsellFlow"}
                          showIdWhen={"always"}
                        />{" "}
                        has been deleted!
                      </Typography>
                    );
                  });
                } catch (e) {
                  // TODO: Toasting system with common error handlers for
                  // - Laravel formRequest authorize denied error
                  // - Laravel findOrFail failure error
                  // - Others?
                  toast.error(() => (
                    <Typography variant={"body2"}>
                      Was unable to delete the pages for{" "}
                      <ModelInstance
                        inheritColor
                        instance={flow}
                        mark={"none"}
                        modelName={"UpsellFlow"}
                        showIdWhen={"always"}
                      />
                    </Typography>
                  ));
                }
              }}
              variant={"outlined"}
            >
              Delete
            </LoadingButton>
          )}
        </Box>
      </Box>
    </FormProvider>
  );
}
